// Security mode only (installed from src/router/index.js). Runs after the existing beforeEach has
// written title, description, Open Graph and Twitter tags, and completes the head for search
// engines and AI crawlers: canonical URL, og:url on the canonical host, sharing image and card,
// robots, no meta keywords, and one JSON-LD graph (Organization on every page, WebSite on the
// home page, BreadcrumbList on inner pages, Service on the service pages).
// The build-time prerender (scripts/seo/prerender.mjs) captures the result into each page's HTML.
import {
  CANONICAL_ORIGIN,
  OG_IMAGE,
  META_OVERRIDES,
  SERVICE_PAGES,
  NOINDEX_ROUTE_NAMES,
} from '@/content/security/seo';
import {
  ORGANIZATION,
  WEBSITE,
  graph,
  breadcrumbJsonLd,
  servicesJsonLd,
  assessmentJsonLd,
} from '@/content/security/jsonld';

// Routes whose sharing image comes from the API (set in beforeEach); every other page uses the
// 1200x630 site image.
const API_IMAGE_ROUTES = ['blogDetails', 'ExperienceDetails', 'LearningHubDetails'];
const JSON_LD_ID = 'ld-site';

const head = () => document.head;

const metaEl = (attr, key) => {
  let el = head().querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    head().appendChild(el);
  }
  return el;
};
const getMeta = (attr, key) => {
  const el = head().querySelector(`meta[${attr}="${key}"]`);
  return el ? el.getAttribute('content') || '' : '';
};
const setMeta = (attr, key, content) => metaEl(attr, key).setAttribute('content', content);
const remove = (selector) => head().querySelectorAll(selector).forEach((el) => el.remove());

const setCanonical = (href) => {
  let link = head().querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    head().appendChild(link);
  }
  link.setAttribute('href', href);
};

// "/about/" and "/about" are the same page; the canonical form has no trailing slash.
const canonicalPath = (path) => path.replace(/\/+$/, '') || '/';

const applyMeta = (meta) => {
  document.title = `${meta.title} | DIGU`;
  setMeta('name', 'description', meta.description);
  setMeta('property', 'og:title', meta.og.title);
  setMeta('property', 'og:description', meta.og.description);
  setMeta('name', 'twitter:title', meta.twitter.title);
  setMeta('name', 'twitter:description', meta.twitter.description);
};

const applyImage = (routeName) => {
  const apiImage = API_IMAGE_ROUTES.includes(routeName) && getMeta('property', 'og:image');
  if (apiImage) {
    remove('meta[property="og:image:width"], meta[property="og:image:height"]');
    remove('meta[property="og:image:alt"], meta[name="twitter:image:alt"]');
    setMeta('name', 'twitter:image', apiImage);
    return;
  }
  setMeta('property', 'og:image', OG_IMAGE.url);
  setMeta('property', 'og:image:width', String(OG_IMAGE.width));
  setMeta('property', 'og:image:height', String(OG_IMAGE.height));
  setMeta('property', 'og:image:alt', OG_IMAGE.alt);
  setMeta('name', 'twitter:image', OG_IMAGE.url);
  setMeta('name', 'twitter:image:alt', OG_IMAGE.alt);
};

// Legacy routes with a title only leave the social tags empty: fill them from the page's own
// title and description, and drop description tags that would otherwise be empty.
const fillGaps = () => {
  const title = document.title;
  const description = getMeta('name', 'description');
  if (!getMeta('property', 'og:title')) setMeta('property', 'og:title', title);
  if (!getMeta('name', 'twitter:title')) setMeta('name', 'twitter:title', title);
  if (description) {
    if (!getMeta('property', 'og:description')) setMeta('property', 'og:description', description);
    if (!getMeta('name', 'twitter:description')) setMeta('name', 'twitter:description', description);
  } else {
    remove('meta[name="description"], meta[property="og:description"], meta[name="twitter:description"]');
  }
};

const pageGraph = (path, meta, indexable) => {
  const nodes = [ORGANIZATION];
  if (path === '/') {
    nodes.push(WEBSITE);
  } else if (indexable) {
    const name = meta.breadcrumb || document.title.replace(/ \| DIGU$/, '');
    nodes.push(
      breadcrumbJsonLd([
        { name: 'Home', path: '/' },
        { name, path },
      ])
    );
  }
  if (path === SERVICE_PAGES.services) nodes.push(...servicesJsonLd(path));
  if (path === SERVICE_PAGES.assessment) nodes.push(...assessmentJsonLd(path));
  return graph(nodes);
};

const writeJsonLd = (data) => {
  let el = document.getElementById(JSON_LD_ID);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = JSON_LD_ID;
    head().appendChild(el);
  }
  // Escape "<" so no string in the data can close the script element.
  el.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
};

export function installSecurityHead(router) {
  router.afterEach((to, from, failure) => {
    if (failure) return;

    const path = canonicalPath(to.path);
    const override = META_OVERRIDES[path];
    if (override) applyMeta(override);
    const meta = override || to.meta || {};
    const indexable = !NOINDEX_ROUTE_NAMES.includes(to.name);

    remove('meta[name="keywords"]');
    fillGaps();

    if (indexable) {
      const canonical = CANONICAL_ORIGIN + path;
      remove('meta[name="robots"]');
      setCanonical(canonical);
      setMeta('property', 'og:url', canonical);
    } else {
      setMeta('name', 'robots', 'noindex,follow');
      remove('link[rel="canonical"], meta[property="og:url"]');
    }

    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'DIGU');
    setMeta('name', 'twitter:card', 'summary_large_image');
    applyImage(to.name);

    writeJsonLd(pageGraph(path, meta, indexable));
  });
}
